// Second-device detection (Deploy I) — turns camera samples into EPISODES (start, end, duration).
// Pure and unit-tested: the analyzer feeds it face samples every ~2 s and phone samples every ~5 s;
// it reports one episode when it ends (or on flush at the end of the interview). Nothing camera-based
// is decided here — the server combines episodes with other signals (never HIGH_RISK on its own).
import { pitchDownDegrees } from './headPose';

export const THRESHOLDS = {
  faceAbsentSeconds: 5,         // no face (in a usable frame) for 5 s
  lookingDownDegrees: 20,       // head pitched down this far below the candidate's own baseline …
  lookingDownSeconds: 3,        // … for at least 3 s
  phoneScore: 0.6,              // "cell phone" confidence
  phoneConsecutive: 2,          // on 2 consecutive phone samples
  multiFaceConsecutive: 2,      // a second (moving) face on 2 consecutive samples
  staticShare: 0.6,             // an extra face in the same spot for ≥60% of the interview's face samples …
  staticMinSamples: 30,         // … and ≥30 samples (~1 min) is a photo/poster → its episode is dropped
  staticCell: 0.05,             // position grid (fraction of the frame) for that check
};

export function createEpisodeTracker({ onEpisode, baselinePitch = null, thresholds = THRESHOLDS }) {
  const T = thresholds;
  let baseline = baselinePitch;
  const autoBaseline = [];                       // no calibration → median of the first samples
  const open = {};                               // type → { start, last, max }
  const cellSeen = new Map();                    // static-face filter (decided when an episode ends)
  let faceSamples = 0;
  let phoneHits = 0;
  let phoneMisses = 0;
  let multiHits = 0;

  const begin = (type, t, extra = {}) => { if (!open[type]) open[type] = { start: t, last: t, ...extra }; };
  const touch = (type, t, score) => {
    if (open[type]) { open[type].last = t; if (score !== undefined) open[type].max = Math.max(open[type].max || 0, score); }
  };
  const end = (type, t, minSeconds = 0) => {
    const ep = open[type];
    if (!ep) return;
    delete open[type];
    if (type === 'multiple_faces' && [...ep.cells].every((c) => {
      const n = cellSeen.get(c) || 0;
      return n >= T.staticMinSamples && n >= T.staticShare * faceSamples;
    })) return;                                    // only a fixture (photo/poster) — not another person
    const endT = Math.max(t, ep.last);
    const duration = Math.round((endT - ep.start) * 10) / 10;
    if (duration < minSeconds) return;
    onEpisode({ type, start_s: Math.round(ep.start), end_s: Math.round(endT), duration_s: duration,
                ...(ep.max !== undefined ? { max_score: Math.round(ep.max * 100) / 100 } : {}) });
  };
  const cellOf = (f) => `${Math.round(f.cx / T.staticCell)}:${Math.round(f.cy / T.staticCell)}`;

  return {
    /** faces: [{ cx, cy, w, h, matrix }] (normalised 0..1); reliable=false for dark / unusable frames */
    face(t, faces, reliable = true) {
      if (!reliable) return;                                             // unusable frame: no conclusion
      // ── absent ──
      if (!faces.length) { begin('face_absent_pending', t); touch('face_absent_pending', t); }
      else if (open.face_absent_pending) {
        const ep = open.face_absent_pending; delete open.face_absent_pending;
        if (t - ep.start >= T.faceAbsentSeconds) { open.face_absent = { start: ep.start, last: t }; end('face_absent', t); }
      }
      if (!faces.length) return;
      // ── multiple faces (a fixture such as a poster is filtered out when the episode ends) ──
      faceSamples += 1;
      const primary = [...faces].sort((a, b) => b.w * b.h - a.w * a.h)[0];
      const others = faces.filter((f) => f !== primary);
      others.forEach((f) => cellSeen.set(cellOf(f), (cellSeen.get(cellOf(f)) || 0) + 1));
      const fixture = (c) => (cellSeen.get(c) || 0) >= T.staticMinSamples && (cellSeen.get(c) || 0) >= T.staticShare * faceSamples;
      const live = others.filter((f) => !fixture(cellOf(f)));
      if (open.multiple_faces) {                       // a position that turned out to be a fixture leaves the episode
        [...open.multiple_faces.cells].forEach((c) => { if (fixture(c)) open.multiple_faces.cells.delete(c); });
        if (!open.multiple_faces.cells.size && !live.length) { delete open.multiple_faces; multiHits = 0; }
      }
      if (live.length) {
        multiHits += 1;
        if (multiHits >= T.multiFaceConsecutive) {
          begin('multiple_faces', t, { cells: new Set() });
          touch('multiple_faces', t);
          live.forEach((f) => open.multiple_faces.cells.add(cellOf(f)));
        }
      } else { multiHits = 0; end('multiple_faces', t); }
      // ── looking down (relative to the candidate's own baseline) ──
      const pitch = pitchDownDegrees(primary.matrix);
      if (pitch === null) return;
      if (baseline === null) {
        autoBaseline.push(pitch);
        if (autoBaseline.length >= 5) baseline = [...autoBaseline].sort((a, b) => a - b)[2];   // median of 5
        return;
      }
      if (pitch - baseline >= T.lookingDownDegrees) { begin('looking_down', t); touch('looking_down', t); }
      else end('looking_down', t, T.lookingDownSeconds);
    },
    /** phones: [{ score }] from the object detector ("cell phone" only) */
    phone(t, phones) {
      const best = phones.reduce((m, p) => Math.max(m, p.score || 0), 0);
      if (best >= T.phoneScore) {
        phoneHits += 1; phoneMisses = 0;
        if (phoneHits >= T.phoneConsecutive) {
          if (!open.phone_detected) open.phone_detected = { start: t - 5, last: t, max: best };   // seen since the previous sample
          touch('phone_detected', t, best);
        }
      } else {
        phoneHits = 0; phoneMisses += 1;
        if (phoneMisses >= 2) end('phone_detected', t);
      }
    },
    /** End of the interview: report every open episode. */
    flush(t) {
      if (open.face_absent_pending && t - open.face_absent_pending.start >= T.faceAbsentSeconds) {
        open.face_absent = open.face_absent_pending;
      }
      delete open.face_absent_pending;
      ['face_absent', 'multiple_faces', 'phone_detected'].forEach((k) => end(k, t));
      end('looking_down', t, T.lookingDownSeconds);
    },
    baseline() { return baseline; },
  };
}
